// Coalesces per-id lookups into a single batched fetch call.
//
// React flushes all of a commit's passive effects synchronously, so every
// component mounting in the same commit calls its queryFn within that one
// synchronous block. Queuing the actual request as a microtask — rather than
// firing it immediately — lets every id enqueued during that block ride the
// same request: the microtask runs the instant the block ends, before the
// browser does anything else (paint, timers, etc), so nothing is missed and
// nothing is delayed beyond what's unavoidable.
//
// Shared by statusBatcher.js and the journal batch fetch — both need the same
// coalescing mechanism, differing only in the fetcher and what a missing id
// should resolve to.
export function createBatchQueue({ missingValue }) {
  let pending = new Map(); // id -> [{ resolve, reject }]
  let scheduled = false;
  let batchFetcher = null; // latest batch-fetch implementation passed to enqueue

  function flush() {
    const batch = pending;
    pending = new Map();
    scheduled = false;

    const ids = Array.from(batch.keys());
    batchFetcher(ids)
      .then((result) => {
        for (const [id, resolvers] of batch) {
          const value = result[id] ?? missingValue;
          resolvers.forEach(({ resolve }) => resolve(value));
        }
      })
      .catch((error) => {
        for (const resolvers of batch.values()) {
          resolvers.forEach(({ reject }) => reject(error));
        }
      });
  }

  return function enqueue(id, fetchBatch) {
    batchFetcher = fetchBatch;

    return new Promise((resolve, reject) => {
      const resolvers = pending.get(id) ?? [];
      resolvers.push({ resolve, reject });
      pending.set(id, resolvers);

      if (!scheduled) {
        scheduled = true;
        queueMicrotask(flush);
      }
    });
  };
}
